import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import AddressManager from '../components/AddressManager';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSiteName } from '../contexts/SiteSettingsContext';
import { Check, MapPin } from 'lucide-react';

const SubscriptionPage = () => {
  const [plans, setPlans] = useState([]);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [addressDialog, setAddressDialog] = useState({ open: false, plan: null });
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const { user } = useAuth();
  const siteName = useSiteName();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans`).then(r => setPlans(r.data)).catch(() => {});
  }, []);

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handleSubscribe = async (plan) => {
    if (!user) { navigate('/login'); return; }
    if (processingPlanId) return;
    // Phase 1: capture address before payment
    setAddressDialog({ open: true, plan });
  };

  const proceedWithPayment = async () => {
    const plan = addressDialog.plan;
    if (!plan) return;
    if (!selectedAddressId) { toast.error('Please choose a delivery address'); return; }
    setAddressDialog({ open: false, plan: null });
    setProcessingPlanId(plan.plan_id);
    try {
      const { data: orderData } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/create-razorpay-order`,
        { plan_id: plan.plan_id, address_id: selectedAddressId },
        { withCredentials: true }
      );
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Failed to load payment gateway'); setProcessingPlanId(null); return; }
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: siteName,
        description: `${orderData.plan_name} subscription`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/verify-payment`,
              { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature },
              { withCredentials: true }
            );
            toast.success(`Subscribed to ${plan.name}! We'll reach out with delivery details.`);
            navigate('/profile');
          } catch (err) {
            // Webhook will still activate it as the authoritative source.
            toast.success('Payment received. Your subscription will be activated shortly via webhook.');
          } finally {
            setProcessingPlanId(null);
          }
        },
        prefill: { name: user?.name || '', email: user?.email || '', contact: '' },
        theme: { color: '#2E7D32' },
        modal: { ondismiss: () => setProcessingPlanId(null) },
      };
      const rz = new window.Razorpay(options);
      rz.on('payment.failed', (resp) => { toast.error('Payment failed: ' + (resp.error?.description || 'unknown error')); setProcessingPlanId(null); });
      rz.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start subscription payment');
      setProcessingPlanId(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative py-24 bg-[#1A237E] text-white overflow-hidden" data-testid="subscription-hero">
        <div className="container mx-auto px-4 text-center relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-accent mb-4">Ritual Subscription</p>
          <h1 className="text-4xl sm:text-5xl font-black font-heading mb-4" data-testid="subscription-heading">Your Monthly Tea Ritual</h1>
          <p className="text-white/60 max-w-lg mx-auto text-base">
            A curated box of premium teas, tasting notes, and brewing guides — delivered to your doorstep every month.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 -mt-8" data-testid="subscription-plans">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.plan_id}
                className="p-8 rounded-2xl bg-white border-2 border-[#E0E0E0] hover:border-primary transition-colors"
                data-testid={`plan-${plan.plan_id}`}
              >
                <h3 className="font-heading font-bold text-xl mb-1">{plan.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
                <p className="text-3xl font-black text-primary mb-1">
                  {'\u20B9'}{plan.price}<span className="text-sm font-normal text-muted-foreground">/{plan.interval}</span>
                </p>
                <ul className="space-y-2 my-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full rounded-full"
                  onClick={() => handleSubscribe(plan)}
                  disabled={processingPlanId === plan.plan_id}
                  data-testid={`subscribe-${plan.plan_id}`}
                >
                  {processingPlanId === plan.plan_id ? 'Processing…' : 'Subscribe Now'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-muted" data-testid="how-it-works">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              { step: '01', title: 'Choose a Plan', desc: 'Pick monthly or quarterly based on your tea journey.' },
              { step: '02', title: 'We Curate', desc: 'Our tea masters hand-pick the finest teas for your box.' },
              { step: '03', title: 'Enjoy & Explore', desc: 'Receive your box with tasting notes and brewing guides.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <span className="text-4xl font-black text-secondary/40 font-heading">{item.step}</span>
                <h3 className="font-heading font-bold mt-2 mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <Dialog open={addressDialog.open} onOpenChange={(v) => !v && setAddressDialog({ open: false, plan: null })}>
        <DialogContent className="max-w-lg" data-testid="subscription-address-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Where should we send your box?</DialogTitle>
            <DialogDescription>
              {addressDialog.plan ? (
                <>Choose the delivery address for your <b>{addressDialog.plan.name}</b> subscription. You can add or edit addresses below.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <AddressManager onSelect={(id) => setSelectedAddressId(id)} selectedId={selectedAddressId} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressDialog({ open: false, plan: null })} data-testid="subscription-address-cancel">Back</Button>
            <Button
              onClick={proceedWithPayment}
              disabled={!selectedAddressId}
              data-testid="subscription-address-continue"
            >
              Continue to Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
